/**
 * Shared vocabulary of a payer charge configuration: one dated row giving the
 * claim setup and prices of one test type's CPT (or of the six tiered HST
 * codes) from its start day on. Both places that edit them use it: the
 * per-payer Charges tab and the cross-payer Charge Rules settings screen.
 *
 * The end of a configuration is derived, never typed: it is the day before
 * the next row of its series starts (or its `endsOn`). Mirrors the backend's
 * charge-configs.util.
 */
import {
  emptyHstTierPrices,
  HST_CODE_COMPONENTS,
  HST_CODE_TIERS,
  HST_CODES_EARLIEST_EFFECTIVE_FROM,
  HstTierPrices,
  isAmount,
  isHstTierPricesComplete,
} from 'components/lab/payer/hstCodes/hstCodes'
import type { HstTierRate } from 'ts-back-end/src/business/payer/dto/payer.dto'

/** A configuration's contracted flag as a select: '' leaves it to the payer's setting. */
export type ContractedChoice = '' | 'yes' | 'no'

export type HstMode = 'GLOBAL' | 'SPLIT'

export type ChargeConfigStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED'

/**
 * One configuration as the backend stores and lists it. `effectiveFrom` is a
 * YYYY-MM-DD day, null only on migrated rows in force since the beginning. A
 * null `billedAmount` bills the next later row's amount in the series, which
 * the listing carries as `billedFallback`.
 */
export type StoredChargeConfig = {
  id: string
  testType: string
  isPrimary: boolean
  effectiveFrom: string | null
  endsOn?: string | null
  hstMode?: HstMode
  /** Null on a Split row, which bills the tiered codes instead. */
  cptCode: string | null
  chargeLocationCode: string
  billingLocationCode?: number
  billingProvider?: string
  hstUnitsBillable?: number
  billedAmount: number | null
  /**
   * Payer only: the billed amount is the old charge rule's own charge, which
   * reports read after a line's own billed rate. The listing shows such a row
   * with a null `billedAmount` and the charge as `billedFallback`.
   */
  billedFromRule?: boolean
  contractRate: number | null
  contracted: boolean | null
  /** Split rows only: exactly six prices, a TC and a PC for every tier. */
  hstTierRates?: HstTierRate[]
  hstSkipFailedStudies?: boolean
  /** Listing only: what a null billed amount bills. */
  billedFallback?: number | null
}

/** A configuration as the form edits it; `id` is absent on a new row. */
export type ChargeConfigFormValues = {
  id?: string
  effectiveFrom: string
  endsOn: string
  testType: string
  hstMode: HstMode
  cptCode: string
  chargeLocationCode: string // 10 | 11 | 81
  billingLocationCode?: number // see payors.ts > BILLING_LOCATION_CODES
  billingProvider?: string
  hstUnitsBillable?: number
  billedAmount: string
  contractRate: string
  contracted: ContractedChoice
  isPrimary: boolean
  /** Split only: the six tier prices. */
  hstPrices: HstTierPrices
  hstSkipFailedStudies: boolean
}

/** The body of POST /lab/payer-charge-rules; with an `id` it edits that row. */
export type SaveChargeConfigRequest = {
  payerId: number
  id?: string
  testType: string
  isPrimary: boolean
  effectiveFrom: string | null
  endsOn: string | null
  hstMode?: HstMode
  cptCode: string | null
  chargeLocationCode: string
  billingLocationCode?: number
  billingProvider?: string
  hstUnitsBillable?: number
  billedAmount?: number | null
  contractRate?: number | null
  contracted?: boolean | null
  hstTierRates?: HstTierRate[]
  hstSkipFailedStudies?: boolean
}

// Years past 9999 are not dates anyone means; without it a date field takes six digits.
export const MAX_RATE_DAY = '9999-12-31'

export const todayIso = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  return `${now.getFullYear()}-${month}-${day}`
}

const isStated = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined

const toAmount = (value: string) => (value.trim() === '' ? null : Number(value))

const amountText = (amount: number | null | undefined) => (isStated(amount) ? String(amount) : '')

export { isAmount }

/** How a contracted flag reads; `payerDefault` resolves one left to the payer. */
export const contractedLabel = (contracted: boolean | null | undefined, payerDefault?: boolean) => {
  const resolved = isStated(contracted) ? contracted : payerDefault

  if (resolved === undefined || resolved === null) {
    return 'Payer setting'
  }

  return resolved ? 'Yes' : 'No'
}

export const CONTRACTED_OPTIONS = [
  { key: 'yes', value: 'Yes' },
  { key: 'no', value: 'No' },
]

export const toContractedChoice = (contracted: boolean | null | undefined): ContractedChoice =>
  isStated(contracted) ? (contracted ? 'yes' : 'no') : ''

export const fromContractedChoice = (choice: string) => (choice === '' ? null : choice === 'yes')

/** Rates read as money everywhere else in billing. */
export const formatRateAmount = (amount: number | null) =>
  amount === null ? 'No rate' : `$${Number(amount).toFixed(2)}`

// A null start is "since the beginning": earlier than any day, which '' is
// when days are compared as text.
const startOf = (config: { effectiveFrom: string | null }) => config.effectiveFrom ?? ''

// Calendar arithmetic in UTC, so a daylight-saving shift can't move a day.
const dayBefore = (day: string) =>
  new Date(new Date(`${day}T00:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10)

/**
 * The series a configuration belongs to: the primary series of its test type,
 * or the alternate series of its test type and CPT. Mirrors the backend.
 */
export const seriesKey = (
  config: Pick<StoredChargeConfig, 'testType' | 'isPrimary' | 'cptCode'>,
) => (config.isPrimary ? `${config.testType}:primary` : `${config.testType}:${config.cptCode}`)

/**
 * The last day a configuration is in force: the earlier of its `endsOn` and
 * the day before the next row of its series starts. Null while it is open.
 */
export const configEnd = (configs: StoredChargeConfig[], config: StoredChargeConfig) => {
  const key = seriesKey(config)
  const start = startOf(config)
  const nextStart = configs
    .filter((row) => seriesKey(row) === key)
    .map(startOf)
    .filter((day) => day > start)
    .sort()[0]
  const ends = [config.endsOn, nextStart ? dayBefore(nextStart) : null]
    .filter((day): day is string => Boolean(day))
    .sort()

  return ends[0] ?? null
}

/**
 * The row that runs longer once `config` is deleted, with its new end: the one
 * before it in its series. Null when deleting changes no other row's end.
 */
export const configExtendedByDelete = (configs: StoredChargeConfig[], config: StoredChargeConfig) => {
  const key = seriesKey(config)
  const previous = configs
    .filter(
      (row) => row.id !== config.id && seriesKey(row) === key && startOf(row) < startOf(config),
    )
    .sort((left, right) => startOf(right).localeCompare(startOf(left)))[0]

  if (!previous) {
    return null
  }

  const end = configEnd(
    configs.filter((row) => row.id !== config.id),
    previous,
  )

  return end === configEnd(configs, previous) ? null : { config: previous, end }
}

/**
 * Whether a configuration stops on its own `endsOn` before anything else in
 * its series would end it: the series is terminated there, rather than handed
 * on to a newer row.
 */
export const endsBeforeSuccessor = (configs: StoredChargeConfig[], config: StoredChargeConfig) => {
  if (!config.endsOn) {
    return false
  }

  const successorEnd = configEnd(configs, { ...config, endsOn: null })

  return successorEnd === null || config.endsOn < successorEnd
}

/**
 * What a row with a null billed amount bills: the amount of the nearest later
 * Global row of its series that states one. Split rows bill tier prices and
 * are passed over. Mirrors the backend's billedFallback, which the listing
 * carries; the payer screen reads its rows off the payer and works it out here.
 */
export const billedFallback = (configs: StoredChargeConfig[], config: StoredChargeConfig) => {
  const key = seriesKey(config)
  const start = startOf(config)
  const next = configs
    .filter(
      (row) =>
        seriesKey(row) === key &&
        startOf(row) > start &&
        row.hstMode !== 'SPLIT' &&
        isStated(row.billedAmount),
    )
    .sort((left, right) => startOf(left).localeCompare(startOf(right)))[0]

  return next ? next.billedAmount : null
}

/** Whether `today` falls before, inside or after a configuration. */
export const configStatus = (
  configs: StoredChargeConfig[],
  config: StoredChargeConfig,
  today = todayIso(),
): ChargeConfigStatus => {
  if (today < startOf(config)) {
    return 'SCHEDULED'
  }

  const end = configEnd(configs, config)

  return end !== null && today > end ? 'ENDED' : 'ACTIVE'
}

/**
 * The primary configuration of a test type in force on `day`: the one charge
 * creation prices from. Undefined when the test type has none that day.
 */
export const primaryConfigOn = (
  configs: StoredChargeConfig[] | undefined,
  testType: string,
  day: string,
) => {
  const all = configs ?? []

  return all.find((config) => {
    if (!config.isPrimary || config.testType !== testType || startOf(config) > day) {
      return false
    }

    const end = configEnd(all, config)

    return end === null || day <= end
  })
}

/**
 * Whether a new row starts out primary: a Split row always is, and otherwise
 * when nothing primary bills its test type today, or when it bills the CPT of
 * the primary in force today — the usual new period of the primary series.
 */
export const defaultsToPrimary = (
  configs: StoredChargeConfig[] | undefined,
  testType: string,
  cptCode: string,
  hstMode: HstMode,
  today = todayIso(),
) => {
  if (testType === 'HST' && hstMode === 'SPLIT') {
    return true
  }

  const primary = primaryConfigOn(configs, testType, today)

  return !primary || (cptCode !== '' && primary.cptCode === cptCode)
}

/**
 * Rows in table order: by test type, the primary series before the alternates,
 * newest start first within a series. Alternates keep each CPT's series
 * together; the primary series ignores its CPT, which may change over time. A
 * row in force since the beginning is the oldest there is. Mirrors the
 * backend list order.
 */
export const sortConfigsForTable = (configs: StoredChargeConfig[]) =>
  [...configs].sort(
    (left, right) =>
      left.testType.localeCompare(right.testType) ||
      Number(right.isPrimary) - Number(left.isPrimary) ||
      (left.isPrimary ? 0 : (left.cptCode ?? '').localeCompare(right.cptCode ?? '')) ||
      startOf(right).localeCompare(startOf(left)),
  )

export const toConfigFormValues = (config?: StoredChargeConfig): ChargeConfigFormValues => {
  const hstPrices = emptyHstTierPrices()

  for (const rate of config?.hstTierRates ?? []) {
    hstPrices[rate.tier][rate.component] = {
      billed: amountText(rate.billedAmount),
      rate: amountText(rate.amount),
    }
  }

  return {
    id: config?.id,
    effectiveFrom: config?.effectiveFrom ?? '',
    endsOn: config?.endsOn ?? '',
    testType: config?.testType ?? '',
    hstMode: config?.hstMode ?? 'GLOBAL',
    cptCode: config?.cptCode ?? '',
    chargeLocationCode: String(config?.chargeLocationCode ?? ''),
    billingLocationCode: config?.billingLocationCode,
    billingProvider: config?.billingProvider,
    hstUnitsBillable: config?.hstUnitsBillable,
    billedAmount: amountText(config?.billedAmount),
    contractRate: amountText(config?.contractRate),
    // A Split row keeps its flag on the tier prices, one for all six.
    contracted: toContractedChoice(
      config?.hstMode === 'SPLIT' ? config.hstTierRates?.[0]?.contracted : config?.contracted,
    ),
    isPrimary: config?.isPrimary ?? false,
    hstPrices,
    hstSkipFailedStudies: Boolean(config?.hstSkipFailedStudies),
  }
}

/**
 * "New from this": a copy of a configuration that is a new row, starting on
 * `start`. Its end is cleared, since the copy's own start moves the span.
 */
export const newFromConfig = (config: StoredChargeConfig, start: string) => {
  const values = toConfigFormValues(config)

  delete values.id

  return { ...values, effectiveFrom: start, endsOn: '' }
}

const isSplit = (values: ChargeConfigFormValues) =>
  values.testType === 'HST' && values.hstMode === 'SPLIT'

/** The POST body for a configuration: only the fields its mode carries. */
export const toSaveRequest = (
  values: ChargeConfigFormValues,
  payerId: number,
): SaveChargeConfigRequest => {
  const request: SaveChargeConfigRequest = {
    payerId,
    ...(values.id ? { id: values.id } : {}),
    testType: values.testType,
    isPrimary: values.isPrimary,
    effectiveFrom: values.effectiveFrom || null,
    endsOn: values.endsOn || null,
    ...(values.testType === 'HST' ? { hstMode: values.hstMode } : {}),
    cptCode: null,
    chargeLocationCode: String(values.chargeLocationCode),
    billingLocationCode: values.billingLocationCode,
    billingProvider: values.billingProvider,
  }

  if (isSplit(values)) {
    // The six prices in the backend's canonical order: TC before PC, low to high.
    request.hstTierRates = HST_CODE_COMPONENTS.flatMap((component) =>
      HST_CODE_TIERS.map((tier) => ({
        component,
        tier,
        billedAmount: Number(values.hstPrices[tier][component].billed),
        amount: toAmount(values.hstPrices[tier][component].rate),
        contracted: fromContractedChoice(values.contracted),
      })),
    )
    request.hstSkipFailedStudies = values.hstSkipFailedStudies

    return request
  }

  request.cptCode = values.cptCode.trim()
  request.billedAmount = toAmount(values.billedAmount)
  request.contractRate = toAmount(values.contractRate)
  request.contracted = fromContractedChoice(values.contracted)

  if (values.testType === 'HST') {
    // A new row defaults to two nights; an edit sends what it has, so a
    // migrated row doesn't silently gain units.
    if (!values.id) {
      request.hstUnitsBillable = Number(values.hstUnitsBillable ?? 2)
    } else if (values.hstUnitsBillable !== undefined && values.hstUnitsBillable !== null) {
      request.hstUnitsBillable = Number(values.hstUnitsBillable)
    }
  }

  return request
}

/**
 * Why the configuration cannot be saved, or null. Mirrors the row checks of
 * the backend's validateChargeConfigRow and the Split and same-start rules of
 * validateChargeConfigs; the rules that need the whole set (a CPT in force
 * twice, an alternate with no primary) are left to the server's message.
 */
export const configFormProblem = (
  values: ChargeConfigFormValues,
  configs: StoredChargeConfig[],
): string | null => {
  const stored = values.id ? configs.find((config) => config.id === values.id) : undefined
  const split = isSplit(values)
  const start = values.effectiveFrom

  if (!values.testType) {
    return 'Choose a test type.'
  }

  if (!values.chargeLocationCode) {
    return 'Choose a place of service.'
  }

  // Only a row migrated in force since the beginning may keep no start.
  if (!start && !(stored && stored.effectiveFrom === null)) {
    return 'A configuration needs a start date.'
  }

  if (values.endsOn && values.endsOn < start) {
    return `The end date ${values.endsOn} is before the start date.`
  }

  if (stored && (stored.testType === 'HST') !== (values.testType === 'HST')) {
    return "An existing configuration can't move to or from HST — add a new one instead."
  }

  if (stored && stored.testType === 'HST' && (stored.hstMode ?? 'GLOBAL') !== values.hstMode) {
    return "The HST billing mode can't change on an existing configuration — add a new one from the switch date."
  }

  if (split) {
    if (!values.isPrimary) {
      return 'A Split configuration must be primary: billing reads tier prices from the primary only.'
    }

    if (start < HST_CODES_EARLIEST_EFFECTIVE_FROM) {
      return `A Split configuration bills the tiered HST codes, which start on or after ${HST_CODES_EARLIEST_EFFECTIVE_FROM}.`
    }

    if (!isHstTierPricesComplete(values.hstPrices)) {
      return 'A Split configuration needs a billed amount for all six tiered codes, a TC and a PC for every tier, and any rate typed must be zero or more.'
    }
  } else {
    if (!values.cptCode.trim()) {
      return 'A configuration needs a CPT code.'
    }

    // A row migrated without a billed amount reads the next row's and may stay so.
    if (values.billedAmount.trim() === '' && !(stored && stored.billedAmount === null)) {
      return 'A configuration needs a billed amount.'
    }

    if (values.billedAmount.trim() !== '' && !isAmount(values.billedAmount)) {
      return 'The billed amount must be zero or more.'
    }

    if (values.contractRate.trim() !== '' && !isAmount(values.contractRate)) {
      return 'The contract rate must be zero or more.'
    }
  }

  const key = seriesKey({ ...values, cptCode: split ? null : values.cptCode.trim() })
  const clash = configs.find(
    (config) => config.id !== values.id && seriesKey(config) === key && startOf(config) === start,
  )

  if (clash) {
    const series = values.isPrimary ? 'primary' : `alternate ${values.cptCode.trim()}`

    return `Another ${values.testType} ${series} configuration already starts ${
      start ? `on ${start}` : 'from the start'
    } — each needs its own start date.`
  }

  return null
}

export const TEST_TYPE_OPTIONS = [
  { key: 'POX', value: 'Pulse Oximetry' },
  { key: 'COX', value: 'Capnography' },
  { key: 'HST', value: 'Home Sleep Test' },
  { key: 'PSG', value: 'In-Lab Sleep Test' },
]

// PSG carries its own rules since TES-2961 (no more borrowing the HST config);
// its codes are distinct studies, so they keep their names.
export const CPT_CODE_OPTIONS = [
  { key: '94762', value: '94762' },
  { key: '95800', value: '95800' },
  { key: '95801', value: '95801' },
  { key: '95806', value: '95806' },
  { key: 'G0398', value: 'G0398' },
  { key: 'G0399', value: 'G0399' },
  { key: 'G0400', value: 'G0400' },
  { key: '95810', value: '95810 — Polysomnography' },
  { key: '95811', value: '95811 — CPAP Titration' },
  { key: '95805', value: '95805 — Multiple Sleep Latency Test' },
]

export const CHARGE_LOCATION_OPTIONS = [
  { key: '11', value: 'Office (11)' },
  { key: '12', value: 'Home (12)' },
  { key: '81', value: 'Indep. Lab (81)' },
]

export const BILLING_PROVIDER_OPTIONS = [
  { key: 'SLEEP_SOLUTIONS_NORTH_FL', value: 'TestSmarter DBA Sleep Solutions of North Florida' },
  { key: 'TEST_SMARTER', value: 'Test Smarter, Inc.' },
]

export const BILLING_PROVIDER_LABELS: { [key: string]: string } = {
  SLEEP_SOLUTIONS_NORTH_FL: 'TestSmarter DBA Sleep Solutions of North Florida',
  // Retired provider; behaves as TestSmarter everywhere.
  SLEEP_SOLUTIONS: 'Test Smarter, Inc.',
  TEST_SMARTER: 'Test Smarter, Inc.',
}

export const HST_UNITS_OPTIONS = [
  { key: '1', value: '1' },
  { key: '2', value: '2' },
  { key: '3', value: '3' },
]

export const chargeLocationLabel = (locationCode: string | undefined) => {
  const strLocationCode = String(locationCode)
  if (strLocationCode === '11') {
    return 'Office (11)'
  } else if (strLocationCode === '12') {
    return 'Home (12)'
  } else if (strLocationCode === '81') {
    return 'Independent Laboratory (81)'
  } else {
    return `Unknown (${strLocationCode})`
  }
}

/** The CPTs a test type may be billed under. */
export const cptCodesForTestType = (testType: string) => {
  if (testType === 'POX' || testType === 'COX') {
    return ['94762']
  }
  if (testType === 'PSG') {
    return ['95810', '95811', '95805']
  }
  return ['95800', '95801', '95806', 'G0398', 'G0399', 'G0400']
}

type MutationError = { data?: { message?: string } }

export const chargeRuleErrorMessage = (error: unknown, fallback: string) =>
  (error as MutationError)?.data?.message || fallback
